import React from "react";
import { FiZap, FiCpu, FiSettings, FiLayers, FiCode, FiSearch, FiMonitor } from "react-icons/fi";
import { SiNextdotjs, SiVercel, SiSpeedtest } from "react-icons/si";
import {
  LuSettings,
  LuUsers,
  LuSmartphone,
  LuShieldCheck,
  LuPercent,
  LuCircleCheck,
  LuShield,
  LuBuilding2,
  LuShoppingBag,
  LuClock,
  LuWallet,
  LuCpu,
  LuClipboardList,
  LuPalette,
  LuCode,
  LuBug,
  LuRocket,
} from "react-icons/lu";

// 1. Hero Block Configuration
export const NextAppHeroData = {
  backgroundImage: "/images/serviceBanners/nextB.webp",
  whiteImage: "/images/serviceBanners/nextWh.webp",
  subTitle: "High-Performance Next.js Solutions",
  title: (
    <>
      SEO-First Full-Stack <br /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Next.js Applications</span>
    </>
  ),
  description:
    "We build high performance Next.js applications with server side rendering, static generation, and edge deployment to deliver scalable full stack solutions with excellent SEO visibility, speed, and user experience.",
  primaryButton: {
    title: "Get a Free Quote",
    onClick: () => console.log("Next Quote clicked"),
  },
  secondaryButton: {
    title: "View Our Work",
    onClick: () => console.log("Next Work clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "SEO Optimized",
      desc: "Built-in SSR for better search engine rankings.",
      iconColor: "#000000",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Core Web Vitals",
      desc: "Optimized for Google performance metrics and speed.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Edge Deployment",
      desc: "Fast global load times with edge network delivery.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Full-Stack Power",
      desc: "Server Actions and API routes in a single framework.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/reactjs.svg",
      top: "10%",
      right: "12%",
      containerSize: 70,
      iconSize: 50,
      glowColor: "rgba(0,0,0,0.35)",
      iconGlow: "rgba(0,0,0,0.9)",
    },
  ],
};

// 2. Stats Block
export const nextStatsData = [
  {
    icon: LuSettings,
    value: "150+",
    label: "Apps Built",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "90+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuSmartphone,
    value: "50M+",
    label: "Downloads",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.9%",
    label: "Crash-Free Apps",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "40%",
    label: "Faster Development",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Client Satisfaction",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

// 3. Solutions & Process Block
export const nextSolutionsAndProcessData = {
  buildSubtitle: "WHAT WE BUILD",
  buildTitle: "Full-Stack Next.js Architectures for Enterprise Applications",
  buildCards: [
    {
      title: "SEO Web Apps",
      desc: "Fast and highly visible web applications optimized for search rankings and search crawling metrics.",
      icon: SiNextdotjs,
      colorClass: "text-[#000000]",
      bgClass: "bg-[#000000]/10",
      borderClass: "border-[#000000]/20 hover:border-[#000000]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(0,0,0,0.25)]",
    },
    {
      title: "SSG & ISR",
      desc: "Static and dynamic content generation for high-speed performance at scale using incremental regeneration.",
      icon: FiLayers,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "Full-Stack Next.js",
      desc: "Build complete applications using server-side logic, integrated database layers, and the React framework.",
      icon: FiCode,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "Edge Commerce",
      desc: "Ultra-fast global eCommerce using edge deployment and edge middleware routing setups.",
      icon: LuShoppingBag,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "API Routes System",
      desc: "Built-in backend APIs for seamless full-stack development without configuration overhead.",
      icon: FiSettings,
      colorClass: "text-[#34d399]",
      bgClass: "bg-[#34d399]/10",
      borderClass: "border-[#34d399]/20 hover:border-[#34d399]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(52,211,153,0.25)]",
    },
    {
      title: "Server Actions",
      desc: "Modern server-side operations for simplified, secure form submission and database communication logic.",
      icon: FiZap,
      colorClass: "text-[#f59e0b]",
      bgClass: "bg-[#f59e0b]/10",
      borderClass: "border-[#f59e0b]/20 hover:border-[#f59e0b]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)]",
    },
  ],
  processSubtitle: "OUR DEVELOPMENT PROCESS",
  processTitle: "Modern Next.js Engineering Lifecycle",
  processSteps: [
    {
      step: "01",
      title: "Routing & Layout",
      description: "Structure Next.js apps using App Router for optimized parallel and nested rendering layout rules.",
      icon: LuClipboardList,
    },
    {
      step: "02",
      title: "Data Strategy",
      description: "Define efficient server-side data fetching, streaming options, and granular caching patterns.",
      icon: LuCode,
    },
    {
      step: "03",
      title: "Performance Optimization",
      description: "Improve Core Web Vitals and asset streaming configurations for a faster and smoother user experience.",
      icon: LuSettings,
    },
    {
      step: "04",
      title: "Server Components",
      description: "Use server components for better security, zero bundle size impact, and reduced client hydration loads.",
      icon: FiLayers,
    },
    {
      step: "05",
      title: "Edge Deployment",
      description: "Deploy applications globally using global edge computing networks for absolute zero-latency content rendering.",
      icon: LuCircleCheck,
    },
    {
      step: "06",
      title: "Build Pipeline Optimization",
      description: "Streamline bundling pipelines and compiler caches for faster deployment cycles and production reliability.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY CHOOSE NEXT.JS?",
  benefitsTitle: "The Modern Standard for Web Applications",
  benefits: [
    "Hybrid SSR and SSG rendering options",
    "Automatic image optimization metrics",
    "Built-in font optimization engines",
    "Tailwind and CSS structural support out-of-the-box",
    "Zero-config Vercel deployment setups",
    "TypeScript-first development pipelines",
    "Secure server-side data handling patterns",
  ],
  logoImage: "/images/serviceBanners/nextBanner.webp",
  whiteLogoImage: "/images/serviceBanners/nextWh.webp",
};

// 4. Portfolio Data
export const nextPortfolioData = {
  subtitle: "OUR CASE STUDIES",
  title: "Next.js Projects Driving Growth",
  buttonText: "View All Projects",
  buttonLink: "#all-projects",
  apps: [
    {
      title: "TechFlow Portal",
      badge: "Enterprise SaaS",
      badgeColor: "bg-[#000000]/10 text-[#000000] border-[#000000]/20",
      desc: "A full-stack project management tool built with Server Components and Prisma.",
      downloads: "SEO 100",
      rating: "4.9",
      image: "/images/webapp-dev.jpg",
    },
    {
      title: "Global Commerce",
      badge: "E-Commerce",
      badgeColor: "bg-[#a855f7]/10 text-[#a855f7] border-[#a855f7]/20",
      desc: "A high-performance storefront with Incremental Static Regeneration (ISR).",
      downloads: "Edge-Ready",
      rating: "5.0",
      image: "/images/fullstack-dev.jpg",
    },
  ],
};

// 5. Technologies Data (Note: icons use keys mapped in your component)
export const nextTechnologiesData = {
  techTitle: "OUR EXTENSIVE TOOL STACK",
  technologies: [
    { name: "Next.js", icon: "ReactJS" }, // Fallback to React icon if Next is missing
    { name: "React", icon: "ReactJS" },
    { name: "Server Components", icon: "Vite" }, // Using Vite key for SVG visuals
    { name: "Prisma", icon: "SQLite" }, // Generic SQL visual
    { name: "Vercel", icon: "Provider" }, // Cloud provider visual
    { name: "TypeScript", icon: "Dart" }, // Type visual
  ],
  trustTitle: "TRUSTED BY ENTERPRISES",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle" },
  ],
};

// 6. About Data
export const nextAboutData = [
  {
    title: "Next JS Development Services for High-Performance Web Applications",
    content: (
      <>
        Modern businesses require web applications that deliver speed, strong visibility, and smooth user experiences across devices. Reliable{" "}
        <b className="text-text-primary font-semibold">next js development services</b> help organizations create scalable applications that improve website performance and support
        long-term digital growth. Many businesses struggle with slow-loading websites and poor search visibility that reduce engagement and conversions. At{" "}
        <b className="text-text-primary font-semibold">Tycho Technologies</b>, Next.js solutions are developed around performance, usability, and business objectives to help
        companies create modern digital products built for growth.
      </>
    ),
  },
  {
    title: "Next JS Development Company for Scalable Digital Solutions",
    content: (
      <>
        Building successful web applications requires technologies that balance performance with flexibility. A trusted{" "}
        <b className="text-text-primary font-semibold">next js development company</b> helps businesses create structured applications that improve responsiveness, simplify user
        interactions, and support future scalability. Companies investing in <b className="text-text-primary font-semibold">next js web development</b> often experience stronger
        performance because modern architectures improve speed and overall digital efficiency. Tycho Technologies focuses on practical Next.js frameworks designed to support
        business goals while improving customer experiences and long-term maintainability.
      </>
    ),
  },
  {
    title: "Hire Next JS Developers for Better Product Performance",
    content: (
      <>
        Digital products need scalable systems that can adapt to changing customer expectations and growing traffic demands. Businesses looking to{" "}
        <b className="text-text-primary font-semibold">hire next js developers</b> often prioritize faster development, better usability, and stronger web performance.
        Organizations aiming to strengthen frontend ecosystems frequently combine modern architectures with{" "}
        <a href="https://tychotechnologies.com/solidjs-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          solidjs development services
        </a>{" "}
        to create flexible and performance-focused digital experiences.
      </>
    ),
  },
  {
    title: "Why Businesses Choose a Next JS Development Company in India",
    content: (
      <>
        The demand for a trusted <b className="text-text-primary font-semibold">next js development company in india</b> continues to grow as businesses seek reliable frameworks
        for scalable web applications. Companies investing in <b className="text-text-primary font-semibold">next js development services india</b> often focus on usability,
        long-term maintainability, and stronger digital performance. Businesses partnering with Tycho Technologies gain access to customized Next.js strategies designed around
        measurable business objectives and user needs. Many organizations also strengthen frontend flexibility through{" "}
        <a href="https://tychotechnologies.com/vuejs-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          vue js development services
        </a>{" "}
        to create connected and future-ready digital ecosystems.
      </>
    ),
  },
];

// 7. FAQs
export const nextFaqs = [
  {
    q: "Why should I hire Next Js developers in India?",
    a: "Hiring Next Js developers in India gives you access to skilled professionals who can build fast, SEO-friendly, and scalable web applications at a cost-effective rate.",
  },
  {
    q: "What is Nextjs development and why is it important?",
    a: "It involves building apps with server-side rendering and optimized performance. It is important because it improves SEO and ensure a smooth user experience.",
  },
];

// Legacy Support
export const nextTechStack = ["Next.js", "React", "Server Components", "Tailwind CSS", "TypeScript", "Prisma", "Vercel"];
